<template>
  <section-wrapper class="contact-form__container">
    <div class="col md:pr-8">
      <h1 class="container__title">
        {{ data.form_title }}
      </h1>

      <div
        v-if="data.form_description"
        class="container__description"
        v-html="data.form_description"
      />
    </div>

    <div class="col">
      <form-wrapper>
        <template v-if="stateError" #error-message>
          <p>{{ $t("contactForm.errorMessage") }}</p>
        </template>

        <template v-if="stateSuccess" #success-message>
          <p>{{ $t("contactForm.successMessage") }}</p>
        </template>

        <div class="row">
          <input-wrapper
            :error="!!v$[CONTACT_FORM_FIELDS.FIRST_NAME]?.$errors.length"
            :input-id="CONTACT_FORM_FIELDS.FIRST_NAME"
            class="form__input"
            required
          >
            <template #label>{{ $t("contactForm.labels.firstName") }}</template>
            <template #error>
              {{ v$[CONTACT_FORM_FIELDS.FIRST_NAME]?.$errors[0].$message }}
            </template>

            <field-input
              v-model="state[CONTACT_FORM_FIELDS.FIRST_NAME] as string"
              :error="!!v$[CONTACT_FORM_FIELDS.FIRST_NAME]?.$errors.length"
              :field-id="CONTACT_FORM_FIELDS.FIRST_NAME"
              :placeholder="$t('contactForm.placeholders.firstName')"
            />
          </input-wrapper>

          <input-wrapper
            :error="!!v$[CONTACT_FORM_FIELDS.LAST_NAME]?.$errors.length"
            :input-id="CONTACT_FORM_FIELDS.LAST_NAME"
            class="form__input"
            required
          >
            <template #label>{{ $t("contactForm.labels.lastName") }}</template>
            <template #error>
              {{ v$[CONTACT_FORM_FIELDS.LAST_NAME]?.$errors[0].$message }}
            </template>

            <field-input
              v-model="state[CONTACT_FORM_FIELDS.LAST_NAME] as string"
              :error="!!v$[CONTACT_FORM_FIELDS.LAST_NAME]?.$errors.length"
              :field-id="CONTACT_FORM_FIELDS.LAST_NAME"
              :placeholder="$t('contactForm.placeholders.lastName')"
            />
          </input-wrapper>
        </div>

        <div class="row">
          <input-wrapper
            :error="!!v$[CONTACT_FORM_FIELDS.SUBJECT]?.$errors.length"
            :input-id="CONTACT_FORM_FIELDS.SUBJECT"
            class="form__input form__input--select"
            required
          >
            <template #label>{{ $t("contactForm.labels.subject") }}</template>
            <template #error>
              {{ v$[CONTACT_FORM_FIELDS.LAST_NAME]?.$errors[0].$message }}
            </template>
            <field-select
              v-model="state[CONTACT_FORM_FIELDS.SUBJECT] as string"
              :error="!!v$[CONTACT_FORM_FIELDS.SUBJECT]?.$errors.length"
              :options="subjects"
              :placeholder="$t('contactForm.placeholders.subject')"
              class="field-select"
            />
          </input-wrapper>

          <input-wrapper
            :error="!!v$[CONTACT_FORM_FIELDS.EMAIL]?.$errors.length"
            :input-id="CONTACT_FORM_FIELDS.EMAIL"
            class="form__input"
            required
          >
            <template #label>{{ $t("contactForm.labels.email") }}</template>
            <template #error>
              {{ v$[CONTACT_FORM_FIELDS.EMAIL]?.$errors[0].$message }}
            </template>
            <field-input
              v-model="state[CONTACT_FORM_FIELDS.EMAIL] as string"
              :error="!!v$[CONTACT_FORM_FIELDS.EMAIL]?.$errors.length"
              :field-id="CONTACT_FORM_FIELDS.EMAIL"
              :placeholder="$t('contactForm.placeholders.email')"
            />
          </input-wrapper>
        </div>

        <input-wrapper
          :error="!!v$[CONTACT_FORM_FIELDS.MESSAGE]?.$errors.length"
          :input-id="CONTACT_FORM_FIELDS.MESSAGE"
          class="form__input"
          required
        >
          <template #label>{{ $t("contactForm.labels.message") }}</template>
          <template #error>
            {{ v$[CONTACT_FORM_FIELDS.MESSAGE]?.$errors[0].$message }}
          </template>

          <field-text-area
            v-model="state[CONTACT_FORM_FIELDS.MESSAGE] as string"
            :error="!!v$[CONTACT_FORM_FIELDS.MESSAGE]?.$errors.length"
            :field-id="CONTACT_FORM_FIELDS.MESSAGE"
            :placeholder="$t('contactForm.placeholders.message')"
          />
        </input-wrapper>

        <template #footer>
          <field-checkbox
            v-model="state[CONTACT_FORM_FIELDS.NEWSLETTER_CONSENT] as boolean"
            :field-id="CONTACT_FORM_FIELDS.NEWSLETTER_CONSENT"
          >
            {{ $t("contactForm.labels.newsletterConsent") }}
          </field-checkbox>

          <app-button
            :loading="stateLoading"
            class="submit-button"
            variant="primary"
            @click="handleSubmit"
          >
            {{ $t("contactForm.labels.submitButton") }}
          </app-button>

          <div ref="recaptchaContainer" />
        </template>
      </form-wrapper>
    </div>
  </section-wrapper>
</template>

<script lang="ts" setup>
import useVuelidate from "@vuelidate/core";
import type { Ref } from "vue";
import AppButton from "~/modules/shared/components/AppButton.vue";
import {
  CONTACT_FORM_FIELDS,
  CONTACT_FORM_VALIDATION_RULES,
} from "~/modules/shared/components/contactForm/ContactForm.constants";
import type {
  ContactFormComponentType,
  ContactFormState,
} from "~/modules/shared/components/contactForm/ContactForm.types";
import FieldCheckbox from "~/modules/shared/components/form/FieldCheckbox.vue";
import FieldInput from "~/modules/shared/components/form/FieldInput.vue";
import FieldSelect from "~/modules/shared/components/form/FieldSelect.vue";
import FieldTextArea from "~/modules/shared/components/form/FieldTextArea.vue";
import FormWrapper from "~/modules/shared/components/form/FormWrapper.vue";
import InputWrapper from "~/modules/shared/components/form/InputWrapper.vue";
import SectionWrapper from "~/modules/shared/components/SectionWrapper.vue";

const props = defineProps<{
  data: ContactFormComponentType;
}>();

const state: Ref<ContactFormState> = ref({
  [CONTACT_FORM_FIELDS.EMAIL]: "",
  [CONTACT_FORM_FIELDS.FIRST_NAME]: "",
  [CONTACT_FORM_FIELDS.LAST_NAME]: "",
  [CONTACT_FORM_FIELDS.MESSAGE]: "",
  [CONTACT_FORM_FIELDS.NEWSLETTER_CONSENT]: false,
  [CONTACT_FORM_FIELDS.SUBJECT]: "",
});
const v$ = useVuelidate(CONTACT_FORM_VALIDATION_RULES, state);
const { create } = useStrapi4();

const recaptchaContainer: Ref<HTMLElement | undefined> = ref();

const stateError = ref(false);
const stateLoading = ref(false);
const stateSuccess = ref(false);

const subjects = computed(() => {
  return props.data.form_subjects.map((subject) => ({
    value: subject.title,
    label: subject.title,
  }));
});

const handleSubmit = async () => {
  stateError.value = false;
  stateLoading.value = false;
  stateSuccess.value = false;

  v$.value.$touch();

  if (v$.value.$error) return;

  try {
    stateLoading.value = true;
    await create("contact", state.value);
    stateSuccess.value = true;
  } catch (err) {
    stateError.value = true;
  } finally {
    stateLoading.value = false;
  }
};

onMounted(async () => {
  await nextTick();
  if (!recaptchaContainer.value) return;

  //   TODO: render recaptcha
});
</script>

<style lang="scss" scoped>
.contact-form__container {
  :deep(.section-inner__container) {
    @apply md:flex-row;
  }

  .col {
    @apply md:w-1/2;

    &:last-child {
      @apply mt-32 md:mt-0 md:pl-8;
    }
  }

  .row {
    @screen md {
      @apply min-w-full flex -mx-4;

      .form__input {
        @apply w-1/2 px-4;
      }
    }
  }

  .container__title {
    @apply lg:w-4/5 mb-16 text-title-md font-bold;
  }

  .container__description {
    @apply lg:w-4/5 text-body-xs font-normal text-primary-400;
  }

  .form__input {
    @apply mb-8;

    &--select {
      @apply z-20;
    }
  }

  .field-select {
    @apply w-full;
  }

  .submit-button {
    @apply mt-12;
  }
}
</style>
